import { useState } from 'react'
import { App, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckOutlined, CloseOutlined, DollarOutlined, RollbackOutlined, SendOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue } from '@/shared/lib/money'
import { RowActions } from '@/shared/ui/table/RowActions'
import { DistributionApproveModal } from './DistributionApproveModal'
import { DistributionPayModal } from './DistributionPayModal'
import { ReasonModal } from './ReasonModal'
import { ShareAmount, SignedAmount } from './ShareAmount'
import {
  KIND_COLOR,
  STATUS_COLOR,
  bpToPercent,
  cancelDistribution,
  markDistributionPayable,
  reverseDistribution,
  type Distribution,
} from './stakeholderApi'

interface DistributionsTableProps {
  rows: readonly Distribution[]
  loading: boolean
  showStakeholder: boolean
  selectable?: boolean
  selected?: readonly string[]
  onSelect?: (ids: string[]) => void
}

type Closing = { kind: 'cancel' | 'reverse'; row: Distribution }

/** Taqsimot qatorlari va ularning amallari: tasdiqlash, to'lovga chiqarish, to'lash, bekor qilish, storno. */
export function DistributionsTable({ rows, loading, showStakeholder, selectable, selected, onSelect }: DistributionsTableProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [approving, setApproving] = useState<Distribution | null>(null)
  const [paying, setPaying] = useState<Distribution | null>(null)
  const [closing, setClosing] = useState<Closing | null>(null)

  const payable = useMutation({
    mutationFn: (id: string) => markDistributionPayable(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all }),
    onError: (error) => void message.error(translateError(error, t)),
  })

  const columns: ColumnsType<Distribution> = [
    ...(showStakeholder
      ? [
          {
            key: 'stakeholder',
            title: t('stakeholder.distribution.stakeholder'),
            width: 220,
            render: (_: unknown, row: Distribution) => `${row.stakeholder.code} · ${row.stakeholder.name}`,
          },
        ]
      : [{ key: 'period', title: t('stakeholder.distribution.period'), width: 100, render: (_: unknown, row: Distribution) => row.periodCode }]),
    {
      key: 'method',
      title: t('stakeholder.distribution.method'),
      width: 120,
      render: (_, row) => t(`stakeholder.method.${row.method}`),
    },
    {
      key: 'kind',
      title: t('stakeholder.distribution.kind'),
      width: 130,
      render: (_, row) => <Tag color={KIND_COLOR[row.kind]}>{t(`stakeholder.distributionKind.${row.kind}`)}</Tag>,
    },
    {
      key: 'net',
      title: t('stakeholder.distribution.netProfit'),
      width: 150,
      align: 'right',
      render: (_, row) => <SignedAmount value={row.netProfit} />,
    },
    {
      key: 'share',
      title: t('stakeholder.distribution.share'),
      width: 90,
      align: 'right',
      render: (_, row) => t('stakeholder.percent', { value: bpToPercent(row.shareBp) }),
    },
    {
      key: 'calculated',
      title: t('stakeholder.distribution.calculated'),
      width: 150,
      align: 'right',
      render: (_, row) => <ShareAmount value={row.calculated} kind={row.kind} />,
    },
    {
      key: 'approved',
      title: t('stakeholder.distribution.approved'),
      width: 150,
      align: 'right',
      render: (_, row) => (row.approved ? <ShareAmount value={row.approved} kind={row.kind} /> : t('common.dash')),
    },
    {
      key: 'paid',
      title: t('stakeholder.distribution.paid'),
      width: 150,
      align: 'right',
      render: (_, row) => {
        if (row.kind === 'LOSS') return t('common.dash')
        if (row.offset.amount === 0) return formatMoneyValue(row.paid)
        return (
          <>
            {formatMoneyValue(row.paid)}
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {t('stakeholder.distribution.offset')}: {formatMoneyValue(row.offset)}
            </Typography.Text>
          </>
        )
      },
    },
    {
      key: 'remaining',
      title: t('stakeholder.distribution.remaining'),
      width: 150,
      align: 'right',
      render: (_, row) => (row.kind === 'LOSS' ? t('common.dash') : formatMoneyValue(row.remaining)),
    },
    {
      key: 'status',
      title: t('stakeholder.distribution.status'),
      width: 130,
      render: (_, row) => <Tag color={STATUS_COLOR[row.status]}>{t(`stakeholder.status.${row.status}`)}</Tag>,
    },
    {
      key: 'by',
      title: t('stakeholder.distribution.officer'),
      width: 160,
      render: (_, row) => (row.approvedBy ? row.approvedBy.name : row.calculatedBy.name),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'approve',
              label: t('stakeholder.action.approve'),
              icon: <CheckOutlined />,
              permission: 'stakeholder.distribution.approve',
              hidden: row.status !== 'CALCULATED',
              onClick: () => setApproving(row),
            },
            {
              key: 'payable',
              label: t('stakeholder.action.markPayable'),
              icon: <SendOutlined />,
              permission: 'stakeholder.distribution.approve',
              hidden: row.status !== 'APPROVED' || row.kind === 'LOSS',
              loading: payable.isPending && payable.variables === row.id,
              onClick: () => payable.mutate(row.id),
            },
            {
              key: 'pay',
              label: t('stakeholder.action.pay'),
              icon: <DollarOutlined />,
              permission: 'stakeholder.pay',
              hidden: row.status !== 'PAYABLE' || row.kind === 'LOSS',
              onClick: () => setPaying(row),
            },
            {
              key: 'cancel',
              label: t('stakeholder.action.cancel'),
              icon: <CloseOutlined />,
              permission: 'stakeholder.manage',
              hidden: row.status !== 'CALCULATED',
              danger: true,
              divider: true,
              onClick: () => setClosing({ kind: 'cancel', row }),
            },
            {
              key: 'reverse',
              label: t('stakeholder.action.reverse'),
              icon: <RollbackOutlined />,
              permission: 'stakeholder.distribution.approve',
              hidden: !reversible(row) || row.paid.amount > 0,
              danger: true,
              divider: true,
              onClick: () => setClosing({ kind: 'reverse', row }),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <Table<Distribution>
        size="small"
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={[...rows]}
        columns={columns}
        scroll={{ x: 1630 }}
        locale={{ emptyText: t('stakeholder.distribution.empty') }}
        rowSelection={
          selectable
            ? {
                selectedRowKeys: [...(selected ?? [])],
                getCheckboxProps: (row) => ({ disabled: row.status !== 'CALCULATED' || row.calculated.amount <= 0 }),
                onChange: (keys) => onSelect?.(keys.map(String)),
              }
            : undefined
        }
      />
      <DistributionApproveModal distribution={approving} onClose={() => setApproving(null)} />
      <DistributionPayModal distribution={paying} onClose={() => setPaying(null)} />
      <ReasonModal
        open={closing !== null}
        title={closing?.kind === 'reverse' ? t('stakeholder.reverse.title') : t('stakeholder.cancel.title')}
        warning={closing?.kind === 'reverse' ? t('stakeholder.reverse.warning') : t('stakeholder.cancel.warning')}
        okText={closing?.kind === 'reverse' ? t('stakeholder.action.reverse') : t('stakeholder.action.cancel')}
        onSubmit={(reason) =>
          closing?.kind === 'reverse'
            ? reverseDistribution(closing.row.id, reason)
            : cancelDistribution(closing?.row.id ?? '', reason)
        }
        onClose={() => setClosing(null)}
      />
    </>
  )
}

function reversible(row: Distribution): boolean {
  return row.status === 'APPROVED' || row.status === 'PAYABLE' || (row.status === 'PAID' && row.offset.amount > 0)
}
